/**
 * ElderBiscuitAnchor — soft-goods prop in each Elder's zone that Minna
 * can knead to enter a Making-Biscuits trance (see
 * `docs/specs/SPEC-making-biscuits.md`).
 *
 * Concrete subclasses:
 *   - `LeoSweaterAnchor`      (Level 3 Clockwork Canopy — green argyle)
 *   - `GusThrowAnchor`        (Level 2 Overgrown Conservatory — knit throw)
 *   - `KittyBedrollAnchor`    (Level 1 Redwood Scrapyard — leather-and-brass bedroll)
 *
 * The base class owns:
 *   - Proximity detection (via `InteractiveObject.distanceTo` + radius)
 *   - Activation plumbing (routes `b` key to the scene's
 *     `MakingBiscuitsMinigame`).
 *   - A `MakingBiscuitsMinigame` pointer — injected at construction so
 *     multiple anchors share a single mini-game instance per scene.
 *   - The scene-time tick that drives the minigame's decay + assist fill.
 *   - A completion-event listener that kicks off `VisionRenderer.play(...)`
 *     with the right sequence + replay flag.
 *
 * Visibility: anchors are hidden until Aura Sight reveals them. Once
 * revealed they stay visible for the rest of the level so the player
 * can walk back to them after the Elder encounter.
 */
import { Scene } from "@babylonjs/core/scene";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { Color3 } from "@babylonjs/core/Maths/math.color";
// Side-effect import: registers `scene.beginAnimation` which the base
// InteractiveObject.reveal()/solve() paths call. Without it Vitest's
// NullEngine scenes throw "scene.beginAnimation is not a function".
import "@babylonjs/core/Animations/animatable";

import { InteractiveObject, InteractiveConfig } from "./InteractiveObject";
import { MakingBiscuitsMinigame } from "../../abilities/minna/MakingBiscuitsMinigame";
import { VisionRenderer } from "../../systems/VisionRenderer";
import { VISIONS } from "../../config/visions";
import { BISCUITS, COLORS } from "../../config/GameConfig";
import { gameEvents } from "../../core/EventBus";
import { GameEvents } from "../../config/events";
import { SaveSystem, BiscuitElder } from "../../systems/SaveSystem";

export interface ElderBiscuitAnchorConfig extends Omit<InteractiveConfig, "size"> {
  /** Which Elder owns this anchor. */
  elder: BiscuitElder;
  /** Shared mini-game instance — one per scene. */
  minigame: MakingBiscuitsMinigame;
  /** Vision renderer — fires on BISCUITS_COMPLETED for this anchor. */
  visionRenderer: VisionRenderer;
  /** Optional override for proximity radius. */
  interactRadius?: number;
  /** Optional size override. */
  size?: Vector3;
  /** Optional local memory artwork; physical furniture has its own owner. */
  modelPath?: string;
}

export abstract class ElderBiscuitAnchor extends InteractiveObject {
  public readonly elder: BiscuitElder;
  protected minigame: MakingBiscuitsMinigame;
  protected visionRenderer: VisionRenderer;
  protected readonly interactRadius: number;
  private unsubCompleted: (() => void) | null = null;
  protected readonly usesModelOverride: boolean;

  constructor(scene: Scene, config: ElderBiscuitAnchorConfig) {
    const size = config.size ?? new Vector3(0.9, 0.3, 0.7);
    // ElderBiscuitAnchors are hidden by default — revealed via Aura
    // Sight. Per SPEC §2: "Placed in each Elder's zone, persistent
    // after main encounter" and "Anchor objects discoverable via
    // Aura Sight (hidden by default, revealed)".
    super(scene, {
      key: config.key,
      position: config.position,
      size,
      hidden: config.hidden ?? true,
      label: config.label ?? `${config.elder} biscuit anchor`,
      requiredAbility: "makingBiscuits",
    });

    this.elder = config.elder;
    this.minigame = config.minigame;
    this.visionRenderer = config.visionRenderer;
    this.interactRadius = config.interactRadius ?? BISCUITS.INTERACT_RADIUS;
    this.usesModelOverride = !!config.modelPath;

    // Build procedural placeholder geometry — subclasses override
    // `buildPlaceholderMesh()` to customize the soft-goods silhouette.
    this.buildPlaceholderMesh();
    if (this.usesModelOverride) this.setChildVisibility(this.state === "hidden" ? 0 : 1);

    // Subscribe to biscuit completion so the vision fires automatically.
    this.unsubCompleted = gameEvents.on(GameEvents.BISCUITS_COMPLETED, (data) => {
      if (data.anchorKey !== this.key) return;
      this.onBiscuitsCompleted();
    });
    if (config.modelPath) void this.loadModelOverride(config.modelPath);
  }

  /** Subclasses override to build the soft-goods placeholder mesh. */
  protected abstract buildPlaceholderMesh(): void;

  /**
   * Whether Minna is close enough to activate. Scenes call this each
   * frame to drive the HUD prompt and to gate the activation key.
   */
  isInInteractRange(position: Vector3): boolean {
    return this.distanceTo(position) <= this.interactRadius;
  }

  /**
   * Activate biscuit mode if Minna is in range. Scenes call this when
   * the player presses the activation key while near the anchor.
   * Returns true if activation happened; false if the minigame is
   * already running (first press was swallowed, second press cancels
   * externally), or if Minna isn't in range.
   */
  tryActivate(minnaPosition: Vector3, options?: { assistMode?: boolean }): boolean {
    if (!this.isInInteractRange(minnaPosition)) return false;
    if (this.state === "hidden") return false; // anchor not revealed yet
    const rootPos = this.rootMesh.position;
    return this.minigame.activate(
      this.elder,
      this.key,
      { x: rootPos.x, y: rootPos.y, z: rootPos.z },
      options,
    );
  }

  /**
   * Fires when the active biscuit session completes (depth reached 100).
   * Picks the first-time-vs-replay variant via SaveSystem and hands it
   * to the VisionRenderer.
   */
  protected onBiscuitsCompleted(): void {
    const replay = this.hasSeenBefore();
    const sequence = VISIONS[this.elder];
    // Fire the visible "you solved this" flash on the anchor mesh so
    // the player has visual confirmation before the fade-to-vision.
    this.solve();
    this.visionRenderer.play(sequence, replay);
  }

  /** Whether this Elder's vision has already been viewed. */
  hasSeenBefore(): boolean {
    try {
      return SaveSystem.getInstance().hasSeenBiscuit(this.elder);
    } catch {
      return false;
    }
  }

  override getHighlightTarget(): AbstractMesh {
    return this.rootMesh.getChildMeshes()[0] ?? this.rootMesh;
  }

  override dispose(): void {
    this.unsubCompleted?.();
    this.unsubCompleted = null;
    super.dispose();
  }

  // ─── Helpers for subclasses ────────────────────────────────────────

  /**
   * Shared soft-goods material — matte fabric, slight emissive tint so
   * the anchor reads as "memory-adjacent" even in dim parlor lighting.
   */
  protected makeFabricMaterial(name: string, diffuse: Color3, emissiveTint = 0.08): StandardMaterial {
    const mat = new StandardMaterial(name, this.scene);
    mat.diffuseColor = diffuse;
    mat.specularColor = new Color3(0.08, 0.08, 0.06);
    mat.emissiveColor = diffuse.scale(emissiveTint);
    return mat;
  }
}

// ─── Concrete anchors ─────────────────────────────────────────────────

/**
 * Leo's green argyle sweater — draped over a parlor chair in the
 * Clockwork Canopy's grove. Canon: `book.html:603` — "cream-tabby
 * gentleman in a green argyle sweater."
 */
export class LeoSweaterAnchor extends ElderBiscuitAnchor {
  protected override buildPlaceholderMesh(): void {
    if (this.usesModelOverride) {
      const lines: Vector3[][] = [];
      for (const z of [-.05, .05]) for (let i=0;i<6;i++) {
        const x=-.15+i*.05;lines.push([new Vector3(x,.008,z),new Vector3(x+.025,.008,z)]);
      }
      const stitches=MeshBuilder.CreateLineSystem(`leoMemory_${this.key}_fallback`,{lines},this.scene);
      stitches.color=Color3.FromHexString("#8c9874");stitches.parent=this.rootMesh;return;
    }

    // Folded sweater — a low, soft rectangle with a peaked fold on top.
    const body = MeshBuilder.CreateBox(
      `leoSweater_${this.key}_body`,
      { width: 0.9, height: 0.22, depth: 0.6 },
      this.scene,
    );
    body.parent = this.rootMesh;
    body.position.y = 0.11;
    body.material = this.makeFabricMaterial(
      `leoSweater_${this.key}_bodyMat`,
      Color3.FromHexString("#3a6b3a"), // emerald argyle green
      0.12,
    );

    const collar = MeshBuilder.CreateBox(
      `leoSweater_${this.key}_collar`,
      { width: 0.4, height: 0.08, depth: 0.18 },
      this.scene,
    );
    collar.parent = this.rootMesh;
    collar.position.set(0.0, 0.25, 0.18);
    collar.material = this.makeFabricMaterial(
      `leoSweater_${this.key}_collarMat`,
      Color3.FromHexString(COLORS.BRASS),
      0.18,
    );
  }
}

/**
 * Gus's woven throw — a knit throw draped over the arm of his leather
 * chair in the Conservatory's post-apex parlor. Canon: `book.html:509-515`.
 */
export class GusThrowAnchor extends ElderBiscuitAnchor {
  protected override buildPlaceholderMesh(): void {
    if (this.usesModelOverride) {
      const lines: Vector3[][] = [];
      for (const z of [-.07, .07]) for (let i = 0; i < 8; i++) {
        const x = -.22 + i * .055;
        lines.push([new Vector3(x, .008, z), new Vector3(x + .027, .008, z)]);
      }
      const stitches = MeshBuilder.CreateLineSystem(`gusMemory_${this.key}_fallback`, { lines }, this.scene);
      stitches.color = Color3.FromHexString("#8f9871"); stitches.parent = this.rootMesh;
      return;
    }
    // Throw + draped corner. Rolled edge on one side sells the "draped"
    // silhouette without needing a cloth simulation.
    const throwBody = MeshBuilder.CreateBox(
      `gusThrow_${this.key}_body`,
      { width: 1.0, height: 0.12, depth: 0.7 },
      this.scene,
    );
    throwBody.parent = this.rootMesh;
    throwBody.position.y = 0.06;
    throwBody.material = this.makeFabricMaterial(
      `gusThrow_${this.key}_bodyMat`,
      Color3.FromHexString("#6b3a2a"), // warm cabernet wool
      0.08,
    );

    const fold = MeshBuilder.CreateBox(
      `gusThrow_${this.key}_fold`,
      { width: 0.6, height: 0.1, depth: 0.18 },
      this.scene,
    );
    fold.parent = this.rootMesh;
    fold.position.set(-0.1, 0.15, 0.28);
    fold.rotation.x = -0.25;
    fold.material = this.makeFabricMaterial(
      `gusThrow_${this.key}_foldMat`,
      Color3.FromHexString("#8a5a34"), // rust-tan complementary stripe
      0.1,
    );
  }
}

/**
 * Kitty's bedroll — leather-and-brass scrapyard bedroll tucked in an
 * alcove near Elder Kitty's spawn. Canon: `book.html:439` — "rugged,
 * battle-scarred tomcat" — the bedroll is the Elder's "travel rig."
 */
export class KittyBedrollAnchor extends ElderBiscuitAnchor {
  protected override buildPlaceholderMesh(): void {
    // Rolled bedroll — a short fat cylinder on its side with a leather
    // strap across the middle.
    const roll = MeshBuilder.CreateCylinder(
      `kittyBedroll_${this.key}_roll`,
      { diameter: 0.44, height: 1.0, tessellation: 20 },
      this.scene,
    );
    roll.parent = this.rootMesh;
    roll.rotation.z = Math.PI / 2;
    roll.position.y = 0.22;
    roll.material = this.makeFabricMaterial(
      `kittyBedroll_${this.key}_rollMat`,
      Color3.FromHexString("#2f2a22"), // deep worn charcoal wool
      0.05,
    );

    const strap = MeshBuilder.CreateBox(
      `kittyBedroll_${this.key}_strap`,
      { width: 0.05, height: 0.55, depth: 0.5 },
      this.scene,
    );
    strap.parent = this.rootMesh;
    strap.position.y = 0.22;
    strap.material = this.makeFabricMaterial(
      `kittyBedroll_${this.key}_strapMat`,
      Color3.FromHexString("#6b4a2a"), // tanned leather
      0.05,
    );

    const buckle = MeshBuilder.CreateBox(
      `kittyBedroll_${this.key}_buckle`,
      { width: 0.08, height: 0.1, depth: 0.12 },
      this.scene,
    );
    buckle.parent = this.rootMesh;
    buckle.position.set(0.0, 0.4, 0.0);
    buckle.material = this.makeFabricMaterial(
      `kittyBedroll_${this.key}_buckleMat`,
      Color3.FromHexString(COLORS.BRASS),
      0.2,
    );
  }
}
